import React, { useState, useEffect } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import Expenses from '../Expenses/Expenses';
import './Purchase.css';


const SUB_TABS = [
  { id: 'Purchase', label: '📋 Purchase' },
  { id: 'PurchaseReturn', label: '↩️ Purchase Returns' },
  { id: 'Expenses', label: '💸 Expenses' },
];

const EMPTY = {
  purchase_date: today(),
  supplier_id: '',
  supplier_name: '',
  status: 'Pending',
  items: [{ product_code: 'HSD', qty: '', rate: '', tank_id: '', vat_per: '', other_charges: '' }],
  file: null
};

const getToken = () => {
  try {
    const u = localStorage.getItem('pg_user');
    return u ? JSON.parse(u).token : '';
  } catch (_) { return ''; }
};

export default function Purchase({ subPage = 'PurchaseOrder' }) {
  const { user, can, canAccess } = useAuth();
  const [tab, setTab] = useState(subPage);
  useEffect(() => { setTab(subPage); setShowForm(false); setEditRow(null); }, [subPage]);

  const { data: suppliers } = useApi(() => api.suppliers.list(), []);
  const { data: records, loading, error, reload } = useApi(() => api.purchases.list(), []);
  const { data: backendReturns, reload: reloadReturns } = useApi(() => api.purchaseReturns.list(), []);
  const { data: tanks } = useApi(() => api.tanks.list(), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [returnRow, setReturnRow] = useState(null); // For creating/editing returns
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [returnConfirm, setReturnConfirm] = useState(null); // For deleting returns

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { product_code: 'HSD', qty: '', rate: '', tank_id: '', vat_per: '', other_charges: '' }] });
  };

  const removeItem = (index) => {
    if (form.items.length === 1) return;
    const newItems = form.items.filter((_, i) => i !== index);
    setForm({ ...form, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    if (field === 'product_code') newItems[index].tank_id = '';
    setForm({ ...form, items: newItems });
  };

  const handleSave = async () => {
    if (!form.supplier_id) { notify('❌ Supplier required'); return; }
    if (form.items.some(it => !it.qty || !it.rate || !it.tank_id)) {
      notify('❌ Qty, Rate and Tank required for all items'); return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({
        purchase_date: form.purchase_date,
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        status: form.status,
        items: form.items
      }));
      if (form.file) fd.append('file', form.file);

      await api.purchases.create(fd);
      notify('✅ Purchase recorded'); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    if (!editRow.tank_id) { notify('❌ Tank required'); return; }
    setSaving(true);
    try {
      await api.purchases.update(editRow.id, {
        purchase_date: editRow.purchase_date,
        supplier_id: editRow.supplier_id,
        supplier_name: editRow.supplier_name,
        product_code: editRow.product_code,
        qty: parseFloat(editRow.qty),
        rate: parseFloat(editRow.rate),
        vat_per: parseFloat(editRow.vat_per || 0),
        other_charges: parseFloat(editRow.other_charges || 0),
        tank_id: parseInt(editRow.tank_id),
        status: editRow.status
      });
      notify('✅ Purchase updated'); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleReceive = async (id) => {
    try { await api.purchases.receive(id); notify('✅ Marked as Received'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };

  const handleDelete = async () => {
    try { await api.purchases.remove(confirm.row.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  // --- Return Logic ---

  const handleReturnAction = (rec) => {
    const remaining = Number(rec.qty) - Number(rec.returned_qty || 0);
    setReturnRow({
      purchase_id: rec.id,
      bill_no: rec.bill_no,
      return_date: today(),
      qty: remaining,
      rate: rec.rate,
      max_qty: remaining,
      mode: 'create'
    });
    setTab('PurchaseReturn');
  };

  const handleEditReturn = (ret) => {
    // Find original purchase to get max limit
    const p = (records || []).find(x => x.id === ret.purchase_id);
    const otherReturned = Number(p?.returned_qty || 0) - Number(ret.qty);
    const max = Number(p?.qty || 0) - otherReturned;

    setReturnRow({
      ...ret,
      max_qty: max,
      mode: 'edit'
    });
  };

  const handleSaveReturn = async () => {
    if (!returnRow.qty || returnRow.qty <= 0) { notify('❌ Invalid quantity'); return; }
    if (Number(returnRow.qty) > Number(returnRow.max_qty)) { notify(`❌ Max returnable: ${returnRow.max_qty}`); return; }

    setSaving(true);
    try {
      if (returnRow.mode === 'create') {
        await api.purchaseReturns.create(returnRow);
        notify('✅ Purchase return recorded');
      } else {
        await api.purchaseReturns.update(returnRow.id, returnRow);
        notify('✅ Purchase return updated');
      }
      setReturnRow(null); reload(); reloadReturns();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDeleteReturn = async () => {
    try {
      await api.purchaseReturns.remove(returnConfirm.id);
      notify('✅ Return deleted'); reload(); reloadReturns();
    } catch (e) { notify('❌ ' + e.message); }
    setReturnConfirm(null);
  };

  const handleViewAttachment = async (id) => {
    try {
      const blob = await api.purchases.downloadAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify('❌ Error: ' + e.message);
    }
  };

  const allRecords = records || [];
  const tableData = tab === 'PurchaseReturn' ? (backendReturns || []) : allRecords;

  const handleExport = () => {
    if (tab === 'Expenses') { notify('❌ Use the Expenses tab\'s own export'); return; }
    const rows = tableData || [];
    if (!rows.length) { notify('❌ No records to export'); return; }
    const printWindow = window.open('', '_blank');
    if (!printWindow) { notify('❌ Pop-up blocked. Please allow pop-ups to export.'); return; }
    const todayStr = new Date().toLocaleDateString();
    const isReturn = tab === 'PurchaseReturn';
    const title = isReturn ? 'Purchase Returns Report' : 'Purchase Records Report';
    const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

    const tableHtml = `
      <html>
        <head>
          <title>${title} - ${todayStr}</title>
          <style>
            body { font-family: 'Nunito', sans-serif; padding: 20px; color: #333; }
            h2 { color: #1a6fad; margin-bottom: 5px; }
            .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background: #f8fafc; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px; border: 1px solid #e2e8f0; text-align: left; }
            td { padding: 10px; border: 1px solid #e2e8f0; font-size: 12px; }
            .text-right { text-align: right; }
            .total-row { background: #f1f5f9; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <div class="meta">Generated on: ${todayStr} | Total Records: ${rows.length} | Total Amount: ₹${fmtNum(totalAmount)}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Bill No</th>
                <th>Supplier</th>
                <th>Product</th>
                <th>Tank</th>
                <th class="text-right">Qty (L)</th>
                <th class="text-right">Rate (₹)</th>
                ${!isReturn ? '<th class="text-right">Basic (₹)</th><th class="text-right">VAT %</th><th class="text-right">Tax (₹)</th><th class="text-right">Other (₹)</th>' : ''}
                <th class="text-right">${isReturn ? 'Amount (₹)' : 'Final (₹)'}</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map((r, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${fmtDate(r.purchase_date || r.return_date)}</td>
                  <td style="font-weight: bold; color: #1a6fad;">${(isReturn ? r.purchase_bill_no : r.bill_no) || ''}</td>
                  <td>${r.supplier_name || '—'}</td>
                  <td>${r.product_code || ''}</td>
                  <td>${r.tank_no || '—'}</td>
                  <td class="text-right">${fmtNum(r.qty)}${!isReturn && r.returned_qty > 0 ? ` (Ret: ${fmtNum(r.returned_qty)})` : ''}</td>
                  <td class="text-right">₹${fmtNum(r.rate)}</td>
                  ${!isReturn ? `<td class="text-right">₹${fmtNum(Number(r.qty || 0) * Number(r.rate || 0))}</td><td class="text-right">${fmtNum(r.vat_per || 0)}%</td><td class="text-right">₹${fmtNum(r.tax_amount || 0)}</td><td class="text-right">₹${fmtNum(r.other_charges || 0)}</td>` : ''}
                  <td class="text-right">₹${fmtNum(r.amount)}</td>
                  <td>${isReturn ? 'Return' : (r.status || '')}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="6" class="text-right">TOTAL</td>
                <td class="text-right">${fmtNum(totalQty)}</td>
                <td></td>
                ${!isReturn ? '<td></td><td></td><td></td><td></td>' : ''}
                <td class="text-right">₹${fmtNum(totalAmount)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <script>window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(tableHtml);
    printWindow.document.close();
  };

  const totalBillAmount = (form?.items || []).reduce((s, it) => {
    const basic = Number(it.qty || 0) * Number(it.rate || 0);
    const tax = basic * Number(it.vat_per || 0) / 100;
    return s + basic + tax + Number(it.other_charges || 0);
  }, 0);
  if (!canAccess('purchases')) {
    return (
      <div className="purchase-page">
        <ErrBanner msg="Access Denied: You do not have permission to access purchases." />
      </div>
    );
  }
  return (
    <div className="purchase-page">
      <div className="page-header">
        <h2 className="page-title">Purchase</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'PurchaseOrder' && can('purchases', 'create') && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>＋ New Purchase</button>
          )}
          {tab !== 'Expenses' && (
            <button className="export-btn" onClick={handleExport}>⬆ Export</button>
          )}
        </div>
      </div>

      <div className="sub-tab-bar">
        {SUB_TABS.map(t => (
          <button key={t.id} className={`sub-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => {
              setTab(t.id);
              setShowForm(false);
              setEditRow(null);
              setReturnRow(null);
              window.location.hash = t.id; // Sync URL
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {tab === 'PurchaseOrder' && showForm && (
        <div className="section-card">
          <div className="section-header">New Purchase Entry</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="form-row" style={{ marginBottom: 16 }}>
              <div className="form-group"><label>Date</label>
                <input type="date" className="form-control" value={form.purchase_date}
                  onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
              <div className="form-group" style={{ flex: 2 }}><label>Supplier</label>
                <select className="form-control" value={form.supplier_id} onChange={e => {
                  const s = (suppliers || []).find(x => String(x.id) === e.target.value);
                  setForm({ ...form, supplier_id: e.target.value, supplier_name: s ? s.name : '' });
                }}>
                  <option value="">-- Select Supplier --</option>
                  {(suppliers || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Status</label>
                <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="Pending">Pending</option>
                  <option value="Received">Received</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 2 }}><label>Attachment (optional)</label>
                <div className="custom-file-upload">
                  {form.file ? `📄 ${form.file.name}` : '📁 Choose File'}
                  <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })} />
                </div>
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Tank</th>
                  <th>Quantity (L)</th>
                  <th>Rate (₹)</th>
                  <th>VAT %</th>
                  <th>Other Charges (₹)</th>
                  <th className="right-align">Tax (₹)</th>
                  <th className="right-align">Final (₹)</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {(form?.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: 4 }}>
                      <select className="form-control" value={item.product_code} onChange={e => updateItem(idx, 'product_code', e.target.value)}>
                        <option value="HSD">HSD (Diesel)</option><option value="MS">MS (Petrol)</option>
                        <option value="CNG">CNG</option><option value="Lube">Lube Oil</option>
                      </select>
                    </td>
                    <td style={{ padding: 4 }}>
                      <select className="form-control" value={item.tank_id} onChange={e => updateItem(idx, 'tank_id', e.target.value)}>
                        <option value="">-- Select Tank --</option>
                        {(tanks || []).filter(t => t.product_code === item.product_code).map(t => (
                          <option key={t.id} value={t.id}>{t.tank_no} ({t.current_stock}L)</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="number" className="form-control" value={item.qty} placeholder="0" onChange={e => updateItem(idx, 'qty', e.target.value)} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="number" className="form-control" value={item.rate} placeholder="0.00" onChange={e => updateItem(idx, 'rate', e.target.value)} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="number" className="form-control" value={item.vat_per} placeholder="0" onChange={e => updateItem(idx, 'vat_per', e.target.value)} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="number" className="form-control" value={item.other_charges} placeholder="0.00" onChange={e => updateItem(idx, 'other_charges', e.target.value)} />
                    </td>
                    <td className="right-align" style={{ padding: 4 }}>
                      ₹{fmtNum(Number(item.qty || 0) * Number(item.rate || 0) * Number(item.vat_per || 0) / 100)}
                    </td>
                    <td className="right-align" style={{ padding: 4, fontWeight: 700 }}>
                      ₹{fmtNum(Number(item.qty || 0) * Number(item.rate || 0) * (1 + Number(item.vat_per || 0) / 100) + Number(item.other_charges || 0))}
                    </td>
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      <button className="btn btn-sm" style={{ color: 'var(--danger)', padding: '2px 6px' }} onClick={() => removeItem(idx)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ Add Another Product</button>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 14, color: 'var(--text-muted)', marginRight: 12 }}>Total Bill Amount:</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary)' }}>₹{fmtNum(totalBillAmount)}</span>
              </div>
            </div>
          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px', borderTop: '1px solid #eee', marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save Purchase'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Purchase — {editRow.bill_no} — {fmtDate(editRow.purchase_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label>
              <input type="date" className="form-control" value={editRow.purchase_date} onChange={e => setEditRow({ ...editRow, purchase_date: e.target.value })} />
            </div>
            <div className="form-group"><label>Supplier</label>
              <select className="form-control" value={editRow.supplier_id} onChange={e => {
                const s = (suppliers || []).find(x => String(x.id) === e.target.value);
                setEditRow({ ...editRow, supplier_id: e.target.value, supplier_name: s ? s.name : '' });
              }}>
                {(suppliers || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value, tank_id: '' })}>
                <option value="HSD">HSD (Diesel)</option><option value="MS">MS (Petrol)</option>
                <option value="CNG">CNG</option><option value="Lube">Lube Oil</option>
              </select>
            </div>
            <div className="form-group"><label>Tank</label>
              <select className="form-control" value={editRow.tank_id} onChange={e => setEditRow({ ...editRow, tank_id: e.target.value })}>
                <option value="">-- Select Tank --</option>
                {(tanks || []).filter(t => t.product_code === editRow.product_code).map(t => (
                  <option key={t.id} value={t.id}>{t.tank_no} ({t.current_stock}L)</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>Quantity (L)</label>
              <input type="number" className="form-control" value={editRow.qty} onChange={e => setEditRow({ ...editRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate (₹)</label>
              <input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} />
            </div>
            <div className="form-group"><label>VAT %</label>
              <input type="number" className="form-control" value={editRow.vat_per ?? ''} placeholder="0" onChange={e => setEditRow({ ...editRow, vat_per: e.target.value })} />
            </div>
            <div className="form-group"><label>Other Charges (₹)</label>
              <input type="number" className="form-control" value={editRow.other_charges ?? ''} placeholder="0.00" onChange={e => setEditRow({ ...editRow, other_charges: e.target.value })} />
            </div>
            {editRow.qty && editRow.rate && (
              <div className="form-group balance-group"><label>Final Amount</label>
                <div className="balance-value">₹{fmtNum(parseFloat(editRow.qty) * parseFloat(editRow.rate) * (1 + Number(editRow.vat_per || 0) / 100) + Number(editRow.other_charges || 0))}</div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : '💾 Update'}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {returnRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--danger)' }}>
          <div className="section-header">{returnRow.mode === 'edit' ? '✏️ Edit' : '↩️'} Purchase Return — {returnRow.purchase_bill_no || returnRow.bill_no} — {fmtDate(returnRow.return_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>Return Date</label>
              <input type="date" className="form-control" value={returnRow.return_date} onChange={e => setReturnRow({ ...returnRow, return_date: e.target.value })} />
            </div>
            <div className="form-group"><label>Qty to Return (Max: {returnRow.max_qty})</label>
              <input type="number" className="form-control" value={returnRow.qty} onChange={e => setReturnRow({ ...returnRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate</label>
              <input type="number" className="form-control" value={returnRow.rate} readOnly style={{ background: '#f9f9f9' }} />
            </div>
            <div className="form-group balance-group"><label>Return Amount</label>
              <div className="balance-value">₹{fmtNum(Number(returnRow.qty || 0) * Number(returnRow.rate || 0))}</div>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-danger" onClick={handleSaveReturn} disabled={saving}>{saving ? 'Saving…' : '💾 Save Return'}</button>
            <button className="btn btn-secondary" onClick={() => setReturnRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {tab === 'Expenses' && <Expenses />}

      {!loading && (tab !== 'Expenses') && (
        <div className="section-card">
          <div className="section-header">
            {tab === 'PurchaseReturn' ? 'Purchase Returns' : 'Purchase Records'}
            {' — Total: ₹'}{fmtNum((tableData || []).reduce((s, r) => s + Number(r.amount || 0), 0))}
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>Date</th><th style={{ textAlign: 'left' }}>Bill No</th>
                <th style={{ textAlign: 'left' }}>Supplier</th><th style={{ textAlign: 'left' }}>Product</th>
                <th style={{ textAlign: 'left' }}>Tank</th>
                <th>Qty (L)</th><th>Rate (₹)</th>
                {tab !== 'PurchaseReturn' && <><th>Basic (₹)</th><th>VAT %</th><th>Tax (₹)</th><th>Other (₹)</th></>}
                <th>{tab === 'PurchaseReturn' ? 'Amount (₹)' : 'Final (₹)'}</th>
                <th style={{ textAlign: 'left' }}>Status</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {tableData.map((r, i) => (
                  <tr key={r.id || i}>
                    <td>{i + 1}</td>
                    <td>{fmtDate(r.purchase_date || r.return_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>
                      {tab === 'PurchaseReturn' ? r.purchase_bill_no : r.bill_no}
                      {r.file_path && (
                        <button
                          onClick={() => handleViewAttachment(r.id)}
                          title="View Attachment"
                          style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                        >📎</button>
                      )}
                    </td>
                    <td>{r.supplier_name || '—'}</td>
                    <td><span className={`badge ${r.product_code === 'MS' ? 'badge-danger' : r.product_code === 'HSD' ? 'badge-info' : 'badge-success'}`}>{r.product_code}</span></td>
                    <td>{r.tank_no || '—'}</td>
                    <td className="right-align">{fmtNum(r.qty)} {tab !== 'PurchaseReturn' && r.returned_qty > 0 && <small style={{ display: 'block', color: 'var(--danger)' }}>Ret: {fmtNum(r.returned_qty)}</small>}</td>
                    <td className="right-align">₹{fmtNum(r.rate)}</td>
                    {tab !== 'PurchaseReturn' && <>
                      <td className="right-align">₹{fmtNum(Number(r.qty || 0) * Number(r.rate || 0))}</td>
                      <td className="right-align">{fmtNum(r.vat_per || 0)}%</td>
                      <td className="right-align">₹{fmtNum(r.tax_amount || 0)}</td>
                      <td className="right-align">₹{fmtNum(r.other_charges || 0)}</td>
                    </>}
                    <td className="right-align">₹{fmtNum(r.amount)}</td>
                    <td><span className={`badge ${r.status === 'Received' ? 'badge-success' : (tab === 'PurchaseReturn' || r.status === 'Return') ? 'badge-danger' : 'badge-warning'}`}>{tab === 'PurchaseReturn' ? 'Return' : r.status}</span></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {tab !== 'PurchaseReturn' && (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {r.status === 'Pending' && can('purchases', 'approve') && (
                            <button title="Receive" style={btnReceive} onClick={() => handleReceive(r.id)}>✅</button>
                          )}
                          {can('purchases', 'update') && (
                            <button title="Edit" style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                          )}
                          {can('purchases', 'delete') && (
                            <button title="Delete" style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>
                          )}
                          {r.status === 'Received' && Number(r.qty) > Number(r.returned_qty || 0) && (
                            <button className="btn btn-sm btn-danger" onClick={() => handleReturnAction(r)} title="Return">↩️</button>
                          )}
                        </div>
                      )}
                      {tab === 'PurchaseReturn' && (
                        <>
                          <button title="Edit" style={btnEdit} onClick={() => handleEditReturn(r)}>✏️</button>
                          <button title="Delete" style={btnDel} onClick={() => setReturnConfirm(r)}>🗑️</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {tableData.length === 0 && (
                  <tr><td colSpan={11} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Purchase Order"
        message={`Delete purchase ${confirm?.row?.bill_no}? ${confirm?.row?.status === 'Received' ? 'WARNING: This will also deduct stock from the tank.' : ''}`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmModal
        isOpen={!!returnConfirm}
        title="Delete Purchase Return"
        message={`Delete this return record for ${returnConfirm?.purchase_bill_no}? Stock and Ledger will be restored.`}
        onConfirm={handleDeleteReturn}
        onCancel={() => setReturnConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
const btnReceive = { background: '#f0fff4', color: '#276749', border: '1px solid #9ae6b4', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };

